import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings } from 'lucide-react';
import apiService from '../../services/apiService';

const fadeIn = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;
const Page = styled.div`min-height:100vh;background:#0a0a0f;color:#e2e8f0;font-family:'Inter','Pretendard',sans-serif;padding:20px;`;
const TopBar = styled.div`display:flex;align-items:center;gap:12px;margin-bottom:28px;padding-bottom:20px;border-bottom:1px solid #1e2030;flex-wrap:wrap;`;
const BackBtn = styled.button`background:none;border:none;color:#64748b;cursor:pointer;padding:8px;border-radius:8px;&:hover{background:#1e2030;color:#e2e8f0}`;
const TitleArea = styled.div`flex:1;min-width:200px;`;
const PageTitle = styled.h1`font-size:22px;font-weight:800;margin:0;background:linear-gradient(135deg,#8b5cf6,#f59e0b);-webkit-background-clip:text;-webkit-text-fill-color:transparent;`;
const PageSub = styled.p`margin:2px 0 0;font-size:12px;color:#64748b;`;
const NavTabs = styled.div`display:flex;gap:8px;flex-wrap:wrap;`;
const NavTab = styled.button`
  background:${p => p.active ? 'rgba(139,92,246,0.15)' : 'transparent'};
  border:1px solid ${p => p.active ? '#8b5cf6' : '#1e2030'};
  color:${p => p.active ? '#8b5cf6' : '#64748b'};
  padding:7px 14px;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;transition:all .2s;
  &:hover{border-color:#8b5cf6;color:#8b5cf6}
`;

const Table = styled.div`background:#111827;border:1px solid #1e2030;border-radius:14px;overflow:hidden;animation:${fadeIn} .4s ease;`;
const TH = styled.div`display:grid;grid-template-columns:120px 90px 1fr 100px 100px 90px;gap:8px;padding:12px 16px;background:#0d1117;font-size:11px;color:#64748b;font-weight:700;text-transform:uppercase;`;
const TR = styled.div`
  display:grid;grid-template-columns:120px 90px 1fr 100px 100px 90px;gap:8px;padding:12px 16px;
  border-bottom:1px solid #1e2030;font-size:13px;transition:background .2s;
  &:hover{background:#1a1f2e}
  &:last-child{border-bottom:none}
`;
const ActionBadge = styled.span`
  font-size:10px;font-weight:700;padding:3px 8px;border-radius:6px;
  background:${p =>
    p.type === 'BUY_BREAKOUT' ? 'rgba(16,185,129,0.15)' :
    p.type?.startsWith('SELL') ? 'rgba(239,68,68,0.15)' :
    'rgba(100,116,139,0.15)'};
  color:${p =>
    p.type === 'BUY_BREAKOUT' ? '#10b981' :
    p.type?.startsWith('SELL') ? '#ef4444' : '#64748b'};
`;

const ACTION_LABEL = {
  BUY_BREAKOUT: '🟢 VCP 매수',
  SELL_STOP_LOSS: '🔴 손절',
  SELL_TRAILING: '🟠 추적 손절',
  SELL_PROFIT: '⚠️ 이격 과다',
};

function MinerviniJournalPage() {
  const navigate = useNavigate();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { setTrades(await apiService.getMinerviniTrades(200)); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const fmt = n => n != null ? Number(n).toLocaleString() : '-';
  const pct = n => n != null ? `${n > 0 ? '+' : ''}${Number(n).toFixed(2)}%` : '-';

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs/minervini')}><ArrowLeft size={18} /></BackBtn>
        <TitleArea>
          <PageTitle>📐 거래 일지</PageTitle>
          <PageSub>VCP 매매 전체 거래 기록</PageSub>
        </TitleArea>
        <NavTabs>
          <NavTab onClick={() => navigate('/labs/minervini')}>대시보드</NavTab>
          <NavTab onClick={() => navigate('/labs/minervini/positions')}>포지션</NavTab>
          <NavTab active onClick={() => navigate('/labs/minervini/journal')}>거래일지</NavTab>
          <NavTab onClick={() => navigate('/labs/minervini/screener')}>스크리너</NavTab>
          <NavTab onClick={() => navigate('/labs/minervini/settings')}><Settings size={13} /></NavTab>
        </NavTabs>
      </TopBar>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#475569' }}>로딩 중...</div>
      ) : trades.length === 0 ? (
        <Table style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>거래 기록이 없습니다.</Table>
      ) : (
        <Table>
          <TH><span>날짜</span><span>종목</span><span>행동</span><span>가격</span><span>손익</span><span>사유</span></TH>
          {trades.map((t, i) => (
            <TR key={t.id || i}>
              <span style={{ color: '#64748b', fontSize: 12 }}>{t.date}</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</div>
                <div style={{ fontSize: 10, color: '#475569' }}>{t.symbol}</div>
              </div>
              <div>
                <ActionBadge type={t.action}>{ACTION_LABEL[t.action] || t.action}</ActionBadge>
                {t.vcpTCount && <span style={{ fontSize: 10, color: '#06b6d4', marginLeft: 6 }}>{t.vcpTCount}T</span>}
              </div>
              <span style={{ fontWeight: 600 }}>{fmt(t.price)}원</span>
              <span style={{ fontWeight: 700, color: t.pnlRate > 0 ? '#10b981' : t.pnlRate < 0 ? '#ef4444' : '#64748b' }}>
                {t.pnlAmount ? `${fmt(t.pnlAmount)}원` : '-'}
                <br/>
                <span style={{ fontSize: 11 }}>{t.pnlRate ? pct(t.pnlRate) : ''}</span>
              </span>
              <span style={{ fontSize: 11, color: '#475569', lineHeight: 1.5 }}>{t.reason || '-'}</span>
            </TR>
          ))}
        </Table>
      )}
    </Page>
  );
}

export default MinerviniJournalPage;
